import type { OnSelectCallbackParams } from '@/components/StructuredVirtualList'

import { useCallback } from 'react'

import { useReadOnly } from '@/hooks'
import { fitMapToSelection } from '@/map'

import { useDataForList } from './useDataForList'

export function useListHandlers(selection: workwave.DeepReadonly<string[]>) {
  const { readonly } = useReadOnly()
  const { structure, setEditing, deselectAll, selectDepots } = useDataForList()

  const handleOnSelect = useCallback(
    (params: OnSelectCallbackParams<'depots'>) => {
      const depotIds: string[] = []

      for (const depotId of params.newSelectedIds) {
        if (depotId.startsWith('group')) continue

        depotIds.push(depotId)
      }

      selectDepots(depotIds)

      // try to select it right away and
      // queue a pending fitMap request
      // it will be applied by the destination view when it opens
      fitMapToSelection({ preventIfVisible: true }, 'add')
    },
    [selectDepots],
  )

  const handleOnSelectAll = useCallback(() => {
    const allSelected = structure.shownItems <= selection.length

    const depotIds: string[] = []

    for (const listItem of structure.list) {
      if (typeof listItem === 'string') {
        depotIds.push(listItem)
      }
    }

    if (allSelected) {
      deselectAll()
    } else {
      selectDepots(depotIds)

      // try to select it right away and
      // queue a pending fitMap request
      // it will be applied by the destination view when it opens
      fitMapToSelection({ preventIfVisible: true }, 'add')
    }
  }, [selectDepots, selection, deselectAll, structure])

  const handleOnDoubleClick = useCallback(() => {
    if (readonly) return

    setEditing(Array.from(selection))
  }, [setEditing, readonly, selection])

  const handleOnClick = useCallback(() => {
    deselectAll()
    setEditing([])
  }, [setEditing, deselectAll])

  return {
    handleOnSelect,
    handleOnSelectAll,
    handleOnDoubleClick,
    handleOnClick,
  }
}
